import type { ProductOption, ShopifyImage } from '@repo/shopify';
import { buildShopifyImageUrl } from '@/lib/shopify-image-url';

/**
 * Color swatches render at ~24-48px. A 96px Shopify variant covers DPR 2
 * comfortably without paying to fetch the full original.
 */
const SWATCH_IMAGE_WIDTH = 96;

export interface ProductVariantSelectionLike {
	id: string;
	availableForSale: boolean;
	selectedOptions: {
		name: string;
		value: string;
	}[];
}

export interface ProductOptionLike {
	id: string;
	name: string;
	optionValues: ProductOption['optionValues'];
	values: string[];
}

interface SearchParamsReader {
	get: (name: string) => string | null;
}

const EXACT_SWATCH_COLORS: Record<string, string> = {
	black: '#111111',
	white: '#f5f5f5',
	gray: '#6b7280',
	grey: '#6b7280',
	'heather gray': '#9ca3af',
	'heather grey': '#9ca3af',
	navy: '#1d4ed8',
	red: '#dc2626',
	blue: '#2563eb',
	green: '#16a34a',
	orange: '#f97316',
	yellow: '#eab308',
	pink: '#ec4899',
	purple: '#9333ea',
	brown: '#92400e',
	tan: '#d2b48c',
	cream: '#f5f5dc',
	natural: '#f5f5dc',
	'hi vis orange': '#ff5f00',
	'hi-vis orange': '#ff5f00',
	'safety orange': '#ff5f00',
	'hi vis yellow': '#d9f500',
	'hi-vis yellow': '#d9f500',
	'safety yellow': '#d9f500',
	'hi vis green': '#39ff14',
	'hi-vis green': '#39ff14',
	'safety green': '#39ff14',
};

function mapVariantSelectedOptions<TVariant extends ProductVariantSelectionLike>(
	variant?: TVariant | null,
) {
	const selectedOptions: Record<string, string> = {};

	for (const option of variant?.selectedOptions || []) {
		selectedOptions[option.name] = option.value;
	}

	return selectedOptions;
}

function countRequestedOptionMatches<TVariant extends ProductVariantSelectionLike>(
	variant: TVariant,
	requestedOptions: Record<string, string>,
) {
	let matches = 0;

	for (const [name, value] of Object.entries(requestedOptions)) {
		if (variant.selectedOptions.some((option) => option.name === name && option.value === value)) {
			matches += 1;
		}
	}

	return matches;
}

export function getColorOption<TOption extends ProductOptionLike>(options: TOption[]) {
	return options.find((option) => option.name.toLowerCase() === 'color') || null;
}

export function getColorOptionValues<TOption extends ProductOptionLike>(options: TOption[]) {
	return getColorOption(options)?.values || [];
}

export function getDefaultSelectedOptions<TVariant extends ProductVariantSelectionLike>(
	variants: TVariant[],
) {
	return mapVariantSelectedOptions(variants.find((variant) => variant.availableForSale));
}

export function getCatalogId(id: string) {
	return id.split('/').pop() || id;
}

export function findVariantByUrlId<TVariant extends ProductVariantSelectionLike>(
	variants: TVariant[],
	variantId: string | null,
) {
	if (!variantId) return null;

	return (
		variants.find(
			(variant) => variant.id === variantId || getCatalogId(variant.id) === variantId,
		) || null
	);
}

export function getRequestedOptionsFromSearchParams(
	searchParams: SearchParamsReader,
	options: ProductOptionLike[],
) {
	const requestedOptions: Record<string, string> = {};

	for (const option of options) {
		const value = searchParams.get(`option.${option.name}`);
		if (value && option.values.includes(value)) {
			requestedOptions[option.name] = value;
		}
	}

	return requestedOptions;
}

export function getSelectedOptionsFromSearchParams<
	TVariant extends ProductVariantSelectionLike,
	TOption extends ProductOptionLike,
>(searchParams: SearchParamsReader, variants: TVariant[], options: TOption[]) {
	const requestedVariantId = searchParams.get('variant');
	if (requestedVariantId !== null) {
		const requestedVariant = findVariantByUrlId(variants, requestedVariantId);
		return requestedVariant?.availableForSale ? mapVariantSelectedOptions(requestedVariant) : {};
	}

	const requestedOptions = getRequestedOptionsFromSearchParams(searchParams, options);
	return resolveSelectedOptions(variants, requestedOptions);
}

export function resolveSelectedVariant<TVariant extends ProductVariantSelectionLike>(
	variants: TVariant[],
	requestedOptions: Record<string, string>,
	fallbackToDefault = true,
) {
	const availableVariants = variants.filter((variant) => variant.availableForSale);
	const defaultVariant = availableVariants[0];
	if (!defaultVariant) return null;

	const requestedCount = Object.keys(requestedOptions).length;
	if (requestedCount === 0) {
		return fallbackToDefault ? defaultVariant : null;
	}

	const exactMatches = availableVariants.filter(
		(variant) => countRequestedOptionMatches(variant, requestedOptions) === requestedCount,
	);
	const exactVariant = exactMatches[0];
	if (exactVariant) {
		return exactVariant;
	}

	const partialMatches = availableVariants
		.map((variant) => ({
			variant,
			matches: countRequestedOptionMatches(variant, requestedOptions),
		}))
		.filter((entry) => entry.matches > 0)
		.sort((a, b) => {
			if (a.matches !== b.matches) return b.matches - a.matches;
			return 0;
		});

	return partialMatches[0]?.variant || (fallbackToDefault ? defaultVariant : null);
}

export function resolveSelectedOptions<TVariant extends ProductVariantSelectionLike>(
	variants: TVariant[],
	requestedOptions: Record<string, string>,
) {
	return mapVariantSelectedOptions(resolveSelectedVariant(variants, requestedOptions) || undefined);
}

export function buildVariantSearchParams(
	variant: Pick<ProductVariantSelectionLike, 'id'> | null | undefined,
) {
	const params = new URLSearchParams();
	if (variant) {
		params.set('variant', getCatalogId(variant.id));
	}

	return params;
}

/**
 * Look up the swatch data for a given color value from Shopify optionValues.
 * Returns `{ color, imageUrl }` from the Shopify swatch if available.
 */
export function getSwatchFromOption<TOption extends ProductOptionLike>(
	options: TOption[],
	colorValue: string,
) {
	const colorOption = getColorOption(options);
	if (!colorOption) return null;

	const optionValue = colorOption.optionValues.find(
		(ov) => ov.name.toLowerCase() === colorValue.trim().toLowerCase(),
	);

	if (!optionValue?.swatch) return null;

	return {
		color: optionValue.swatch.color,
		imageUrl: optionValue.swatch.image?.previewImage?.url ?? null,
	};
}

/**
 * Get the swatch display for a color value.
 * Priority: Shopify swatch image > Shopify swatch color > hardcoded fallback.
 */
export function getColorSwatchStyle(
	colorValue: string,
	options?: ProductOptionLike[],
): { backgroundColor?: string; backgroundImage?: string; backgroundSize?: string } {
	if (options) {
		const swatch = getSwatchFromOption(options, colorValue);
		if (swatch?.imageUrl) {
			const sized = buildShopifyImageUrl(swatch.imageUrl, { width: SWATCH_IMAGE_WIDTH });
			return {
				backgroundImage: `url(${sized})`,
				backgroundSize: 'cover',
			};
		}
		if (swatch?.color) {
			return { backgroundColor: swatch.color };
		}
	}

	return { backgroundColor: getColorSwatchValue(colorValue) };
}

export function getColorSwatchValue(color: string) {
	const normalized = color.trim().toLowerCase();
	const exactMatch = EXACT_SWATCH_COLORS[normalized];
	if (exactMatch) return exactMatch;
	if (normalized.includes('orange')) return '#ff5f00';
	if (normalized.includes('yellow')) return '#d9f500';
	if (normalized.includes('green')) return '#39ff14';
	if (normalized.includes('blue')) return '#2563eb';
	if (normalized.includes('red')) return '#dc2626';
	if (normalized.includes('pink')) return '#ec4899';
	if (normalized.includes('purple')) return '#9333ea';
	if (normalized.includes('brown') || normalized.includes('tan')) return '#b45309';
	if (normalized.includes('gray') || normalized.includes('grey')) return '#9ca3af';
	if (
		normalized.includes('white') ||
		normalized.includes('cream') ||
		normalized.includes('natural')
	) {
		return '#f5f5f5';
	}

	return normalized.replace(/\s+/g, '');
}

/**
 * Normalize a color string for comparison by lowercasing and stripping
 * spaces, hyphens, and underscores. This allows alt tag values like
 * "SafetyOrange" to match Shopify option values like "Safety Orange".
 */
function normalizeColorForComparison(color: string): string {
	return color.toLowerCase().replace(/[\s\-_]+/g, '');
}

/**
 * Parse color from an image alt tag.
 * Format: "Description#color_" e.g. "T-shirt front view#SafetyOrange_"
 * Returns the raw color string (lowercase) or null.
 */
export function getColorFromAltTag(altText: string | null): string | null {
	if (!altText) return null;
	const match = altText.match(/#([^_]+)_/);
	return match?.[1]?.toLowerCase().trim() ?? null;
}

/**
 * Filter product images to only those matching the selected color,
 * based on the `#color_` convention in image alt text.
 * Comparison is fuzzy: "SafetyOrange" matches "Safety Orange".
 * If no images match (e.g. no alt tags use the convention), returns all images.
 */
export function filterImagesByColor(images: ShopifyImage[], selectedColor: string | null) {
	if (!selectedColor) return images;

	const normalizedColor = normalizeColorForComparison(selectedColor);
	const filtered = images.filter((image) => {
		const imageColor = getColorFromAltTag(image.altText);
		if (!imageColor) return false;
		return normalizeColorForComparison(imageColor) === normalizedColor;
	});

	return filtered.length > 0 ? filtered : images;
}
